// Shared day-grid geometry for the dispatch/schedule views.
//
// Pure helpers extracted so the Schedule board and (later) Dispatch Board 5 can
// share one source of truth for the time-of-day grid. Times are naive local
// wall-clock ISO strings ("YYYY-MM-DDTHH:MM:SS") — the dispatch /blocks API and
// B_TAVAILABILITY both emit this, so no timezone handling is needed here.

export const PIXELS_PER_MIN = 1.0;
// Default visible window; expands outward (toward 00:00 / 24:00) when a block
// falls outside it — see dayWindow().
export const DEFAULT_START_HOUR = 8;
export const DEFAULT_END_HOUR = 19;

export function pad2(n: number): string {
  return n.toString().padStart(2, "0");
}

export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function parseIsoDate(s: string): Date {
  const datePart = s.split("T")[0] ?? s;
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

export function shiftDate(s: string, deltaDays: number): string {
  const d = parseIsoDate(s);
  d.setDate(d.getDate() + deltaDays);
  return isoDate(d);
}

export function todayIso(): string {
  return isoDate(new Date());
}

// ── Month-view helpers (Schedule board's Month toggle) ───────────────────

export function monthStartIso(s: string): string {
  const d = parseIsoDate(s);
  return isoDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function monthEndIso(s: string): string {
  const d = parseIsoDate(s);
  return isoDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

export function daysInMonth(s: string): number {
  const d = parseIsoDate(s);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/** Shift by N calendar months, clamping the day-of-month to that month's max
 *  (so 31 Jan + 1mo → 28/29 Feb, not 3 Mar). Returns first-of-month so the
 *  month-view nav lands cleanly. */
export function shiftMonthFirst(s: string, deltaMonths: number): string {
  const d = parseIsoDate(s);
  return isoDate(new Date(d.getFullYear(), d.getMonth() + deltaMonths, 1));
}

/** "June 2026" */
export function fmtMonthYear(s: string): string {
  return parseIsoDate(s).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
}

// "Monday 1 June 2026"
export function fmtLongDate(s: string): string {
  return parseIsoDate(s).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// "13:30" from an ISO datetime string.
export function isoHM(iso: string): string {
  return (iso.split("T")[1] ?? "").slice(0, 5);
}

// Minutes from the grid's start hour to the given ISO datetime's time.
export function minutesFromDayStart(iso: string, startHour: number): number {
  const t = iso.split("T")[1] ?? "00:00:00";
  const [h, m] = t.split(":").map(Number);
  return ((h ?? 0) - startHour) * 60 + (m ?? 0);
}

export function colHeight(startHour: number, endHour: number): number {
  return (endHour - startHour) * 60 * PIXELS_PER_MIN;
}

// Map a [startMin, endMin] interval (minutes from startHour) to a top/height in
// px, clamped to the visible window. Returns null for zero/negative height.
export function clampToCol(
  minStart: number,
  minEnd: number,
  startHour: number,
  endHour: number
): { top: number; height: number } | null {
  const colMax = (endHour - startHour) * 60;
  const a = Math.max(0, Math.min(colMax, minStart));
  const b = Math.max(0, Math.min(colMax, minEnd));
  if (b <= a) return null;
  return { top: a * PIXELS_PER_MIN, height: (b - a) * PIXELS_PER_MIN };
}

export function snap30(minutes: number): number {
  return Math.round(minutes / 30) * 30;
}

// Compute the visible [startHour, endHour] window for a day from its blocks:
// default 08:00–19:00, widened (floor start / ceil end, clamped 0–24) so any
// early/late job or availability block is fully visible.
export function dayWindow(
  blocks: Array<{ start: string; end: string; allDay?: boolean }>
): { startHour: number; endHour: number } {
  let startHour = DEFAULT_START_HOUR;
  let endHour = DEFAULT_END_HOUR;
  for (const b of blocks) {
    if (b.allDay) continue; // all-day blocks shouldn't drag the window to 0–24
    const sh = Number((b.start.split("T")[1] ?? "").slice(0, 2));
    const et = b.end.split("T")[1] ?? "";
    const eh = Number(et.slice(0, 2));
    const em = Number(et.slice(3, 5));
    if (Number.isFinite(sh)) startHour = Math.min(startHour, Math.floor(sh));
    if (Number.isFinite(eh)) endHour = Math.max(endHour, em > 0 ? eh + 1 : eh);
  }
  return {
    startHour: Math.max(0, startHour),
    endHour: Math.min(24, Math.max(endHour, startHour + 1)),
  };
}

export function hourLabels(startHour: number, endHour: number): number[] {
  return Array.from({ length: endHour - startHour + 1 }, (_, i) => startHour + i);
}
